import type { ReactNode } from "react";

import { useContext, createContext } from "react";
import { useOwnedWorkspaceId } from "@/actions/workspace";
import { useAuthContext } from "@/hooks/use-auth-context";

// Types
type WorkspaceContextType = {
  workspaceId: string | null;
  isLoading: boolean;
  error: Error | null;
  refresh: () => void;
};

// Default context value
const defaultContextValue: WorkspaceContextType = {
  workspaceId: null,
  isLoading: true,
  error: null,
  refresh: () => {},
};

// Create context
const WorkspaceContext =
  createContext<WorkspaceContextType>(defaultContextValue);

// Provider component
export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthContext();
  const { workspaceId, isLoading, error, refresh } = useOwnedWorkspaceId(
    user?.id || null
  );

  return (
    <WorkspaceContext.Provider
      value={{
        workspaceId: workspaceId || null,
        isLoading,
        error,
        refresh,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

// Hook for using the workspace context
export const useWorkspace = () => useContext(WorkspaceContext);

// Custom hook for requiring a workspace
// Useful for components that must have a workspace to function
export const useRequiredWorkspace = () => {
  const { workspaceId, isLoading, error, refresh } = useWorkspace();

  // Derived state to indicate if we're still determining if there's a workspace
  const isPending = isLoading || (!workspaceId && !error);

  return {
    workspaceId,
    isLoading,
    error,
    isPending,
    refresh,
  };
};
